import { ArrowUp, ArrowDown } from "lucide-react";
import useTranslation from "../client/hooks/useTranslation";
import type { OfferVM, FirmVM, ComparatorFilters, SortKey } from "./lib/types";
import ComparatorOfferRow from "./ComparatorOfferRow";
import ComparatorOfferCard from "./ComparatorOfferCard";

interface ResultsProps {
  offers: OfferVM[]; // already filtered + sorted
  totalCount: number;
  firms: Map<string, FirmVM>;
  now: Date;
  filters: ComparatorFilters;
  onSortChange: (key: SortKey) => void;
  selectedIds: Set<string>;
  onToggleCompare: (id: string) => void;
}

const headerClass =
  "font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground text-left py-2 px-3";

export default function ComparatorResults({
  offers,
  totalCount,
  firms,
  now,
  filters,
  onSortChange,
  selectedIds,
  onToggleCompare,
}: ResultsProps) {
  const t = useTranslation();

  const SortableHeader = ({
    sortKey,
    label,
    className,
  }: {
    sortKey: SortKey;
    label: string;
    className?: string;
  }) => {
    const active = filters.sortKey === sortKey;
    return (
      <th className={headerClass}>
        <button
          type="button"
          onClick={() => onSortChange(sortKey)}
          className={`inline-flex items-center gap-1 hover:text-foreground ${
            active ? "text-foreground" : ""
          } ${className ?? ""}`}
        >
          {label}
          {active &&
            (filters.sortDir === "asc" ? (
              <ArrowUp className="h-3 w-3" />
            ) : (
              <ArrowDown className="h-3 w-3" />
            ))}
        </button>
      </th>
    );
  };

  return (
    <div>
      {/* Counter */}
      <p className="text-muted-foreground text-sm mb-4">
        {t.comparator.resultsCount
          .replace("{shown}", String(offers.length))
          .replace("{total}", String(totalCount))}
      </p>

      {offers.length === 0 ? (
        <p className="text-muted-foreground py-8 text-center">
          {t.comparator.noResults}
        </p>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="border-b border-border">
                  <SortableHeader sortKey="firm" label={t.comparator.colFirm} />
                  <SortableHeader sortKey="size" label={t.comparator.colSize} />
                  <SortableHeader sortKey="price" label={t.comparator.colPrice} />
                  <th className={headerClass}>{t.comparator.colBilling}</th>
                  <SortableHeader
                    sortKey="activation"
                    label={t.comparator.colActivation}
                  />
                  <th className={headerClass}>{t.comparator.colPlatforms}</th>
                  <th className={headerClass}>{t.comparator.colDrawdown}</th>
                  <th className={headerClass}>{t.comparator.colTarget}</th>
                  <th className={headerClass}>{t.comparator.colRoundTrip}</th>
                  <SortableHeader sortKey="rating" label={t.comparator.colRating} />
                  <th className={headerClass}>{t.comparator.colAction}</th>
                </tr>
              </thead>
              <tbody>
                {offers.map((offer) => {
                  const firm = firms.get(offer.firmId);
                  if (!firm) return null;
                  return (
                    <ComparatorOfferRow
                      key={offer.id}
                      offer={offer}
                      firm={firm}
                      now={now}
                      isSelected={selectedIds.has(offer.id)}
                      onToggleCompare={onToggleCompare}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {offers.map((offer) => {
              const firm = firms.get(offer.firmId);
              if (!firm) return null;
              return (
                <ComparatorOfferCard
                  key={offer.id}
                  offer={offer}
                  firm={firm}
                  now={now}
                  isSelected={selectedIds.has(offer.id)}
                  onToggleCompare={onToggleCompare}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
